import { Domain as D } from 'auth-common/domain';
import {
  AUTH_REQUIREMENT_UNSATISFIED_CODE,
  type AuthRequirementErrorResponse,
  isAuthRequirementErrorResponse,
} from 'auth-common/shared/authRequirements';
import type { AuthInfo } from '../hook/model/authInfo';
import { route } from '../router';
import { redirectToSamlRequired, resolveOrganizationIdForSamlRedirect } from './samlEnforcement';

import AuthRequirements = D.Object.AuthRequirements;

export const MFA_REQUIRED_ERROR_CODE = AUTH_REQUIREMENT_UNSATISFIED_CODE;
export const SSO_REQUIRED_ERROR_CODE = AUTH_REQUIREMENT_UNSATISFIED_CODE;

type RecordLike = Record<string, unknown>;

const toRecord = (value: unknown): RecordLike | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  return value as RecordLike;
};

const extractAuthRequirementPayload = (error: unknown): AuthRequirementErrorResponse | undefined => {
  const candidates = [
    error,
    toRecord(error)?.error,
    toRecord(error)?.data,
    toRecord(error)?.body,
    toRecord(toRecord(error)?.response)?.data,
  ];
  for (const candidate of candidates) {
    if (isAuthRequirementErrorResponse(candidate)) return candidate;
  }
  return undefined;
};

const isMfaRequiredPayload = (value: unknown): boolean => {
  const record = toRecord(value);
  if (!record) return false;
  return isAuthRequirementErrorResponse(record) && record.requirement === 'mfa';
};

const isSsoRequiredPayload = (value: unknown): boolean => {
  const record = toRecord(value);
  if (!record) return false;
  return isAuthRequirementErrorResponse(record) && record.requirement === 'sso';
};

export class MfaRequiredError extends Error {
  public readonly code = MFA_REQUIRED_ERROR_CODE;
  public readonly requirement = 'mfa';

  constructor(message = 'MFA is required for this project.') {
    super(message);
    this.name = 'MfaRequiredError';
  }
}

export class SsoRequiredError extends Error {
  public readonly code = SSO_REQUIRED_ERROR_CODE;
  public readonly requirement = 'sso';
  public readonly projectId: string | undefined;
  public readonly organizationId: string | undefined;

  constructor(params?: { projectId?: string; organizationId?: string; message?: string }) {
    super(params?.message ?? 'SSO requirement is unsatisfied for this organization membership.');
    this.name = 'SsoRequiredError';
    this.projectId = params?.projectId;
    this.organizationId = params?.organizationId;
  }
}

export const isMfaRequiredError = (error: unknown): boolean => {
  if (error instanceof MfaRequiredError) return true;
  if (isMfaRequiredPayload(error)) return true;

  const record = toRecord(error);
  return (
    isMfaRequiredPayload(record?.error) ||
    isMfaRequiredPayload(record?.data) ||
    isMfaRequiredPayload(record?.body) ||
    isMfaRequiredPayload(toRecord(record?.response)?.data)
  );
};

export const isSsoRequiredError = (error: unknown): boolean => {
  if (error instanceof SsoRequiredError) return true;
  if (isSsoRequiredPayload(error)) return true;

  const record = toRecord(error);
  return (
    isSsoRequiredPayload(record?.error) ||
    isSsoRequiredPayload(record?.data) ||
    isSsoRequiredPayload(record?.body) ||
    isSsoRequiredPayload(toRecord(record?.response)?.data)
  );
};

export const throwIfMfaRequiredError = (error: unknown): void => {
  if (isMfaRequiredError(error)) throw new MfaRequiredError();
};

export const throwIfAuthRequirementError = (error: unknown, context?: { projectId?: string }): void => {
  if (isMfaRequiredError(error)) throw new MfaRequiredError();
  if (!isSsoRequiredError(error)) return;

  const payload = extractAuthRequirementPayload(error);
  throw new SsoRequiredError({
    projectId: context?.projectId ?? payload?.projectId,
    organizationId: payload?.organizationId,
  });
};

export const isAuthRequirementError = (error: unknown): boolean => isMfaRequiredError(error) || isSsoRequiredError(error);

export const redirectForSsoRequiredError = (params: {
  error: unknown;
  organizations?: AuthInfo.Organization[];
  redirect?: string | undefined;
}): void => {
  if (!isSsoRequiredError(params.error)) return;

  const ssoError = params.error instanceof SsoRequiredError ? params.error : undefined;
  const payload = ssoError ? undefined : extractAuthRequirementPayload(params.error);
  const organizationId = resolveOrganizationIdForSamlRedirect({
    organizations: params.organizations ?? [],
    projectId: ssoError?.projectId ?? payload?.projectId,
    organizationId: ssoError?.organizationId ?? payload?.organizationId,
  });
  if (!organizationId) return;

  redirectToSamlRequired({
    organizationId,
    redirect: params.redirect ?? (typeof window === 'undefined' ? undefined : `${window.location.pathname}${window.location.search}`),
  });
};

export const isMfaRequiredAndUnsatisfied = (requirements: Pick<AuthRequirements, 'mfa'>): boolean => {
  return AuthRequirements.IsMfaRequiredAndUnsatisfied(requirements);
};

export const getMfaEnrollmentUrl = (returnTo: string | undefined = typeof window === 'undefined' ? undefined : window.location.href) => {
  return route('/account/authentication', {
    query: {
      reason: 'mfa_required',
      ...(returnTo ? { returnTo } : {}),
    },
  });
};

export const redirectToMfaEnrollment = (): void => {
  if (typeof window === 'undefined') return;
  window.location.href = getMfaEnrollmentUrl();
};
